import { AssistTeamPanel } from "@/features/assist-policy/AssistPolicyPanels";
import { GuestLinksSection } from "@/features/guest-links/GuestLinksSection";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  getAppSettings,
  getTeamStreamKeyAudit,
  rotateTeamStreamKey,
  setTeamVisibility,
} from "@/lib/api";
import type { TeamSettings, TeamSettingValidationError } from "@/lib/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  invalidateScope,
  publicAppSettingsKey,
  unscopedKey,
} from "@/lib/queryClient";
import { PageHeader } from "./components/PageHeader";
import { SettingChipGroup } from "./components/SettingControls";
import {
  DEFAULTS_KEYS,
  diffTeamSettings,
  isTeamSettingsDirty,
  projectEditableTeamSettings,
} from "./settingsDiff";
import {
  useTeamSettings,
  useTeamSettingsMutation,
} from "./hooks/useTeamSettings";
import {
  GOAL_VISIBILITY_LABELS,
  VISIBILITY_LABELS,
} from "./visibilityVocabulary";

/**
 * Coach > Settings — the 7th coach tab (M6b).
 *
 * Manual coaching defaults remain editable here. Legacy `ai.*`
 * settings may still arrive from the API, but this page does not
 * expose or submit them.
 *
 * Controls are rendered from the server `schema` payload. The
 * deliberate divergence:
 * `summaries.default_visibility` drops the `player` choice
 * (`SUMMARY_VISIBILITY_OMIT`), because a `player`-visibility
 * summary is invisible to viewers — same call the M6a
 * `MatchSummaryComposerModal` fix made. See the M6b inventory Q1.
 *
 * `can_edit` (from the GET payload) gates the whole surface:
 * `false` for plain coaches ⇒ every control disabled + a "Read
 * only" pill + "ask a team admin" copy. The PATCH is also
 * capability-gated server-side — the disabled UI is the
 * primary guard, the 403 a backstop.
 *
 * Local edit state is projected to the visible defaults and reset
 * whenever a fresh payload arrives (initial load, post-save,
 * scope change).
 */

/** `summaries.default_visibility` choices to hide — see the file
 * docstring + M6b inventory Q1. */
const SUMMARY_VISIBILITY_OMIT = new Set(["player"]);

/** Human labels for the manual defaults exposed on this page. */
const SETTING_LABELS: Record<string, { label: string; description: string }> = {
  "notes.default_visibility": {
    label: "New note visibility",
    description: "The visibility a new coaching note starts at.",
  },
  "summaries.default_visibility": {
    label: "Match recap visibility",
    description: "The visibility a new match recap starts at.",
  },
  "goals.default_visibility": {
    label: "Goal visibility",
    description: "The visibility a new player goal starts at.",
  },
};

/** Human labels for visibility choices. */
const CHOICE_LABELS: Record<string, string> = {
  // The default a team admin picks here reads identically where a
  // coach meets it in the corresponding composer.
  private: VISIBILITY_LABELS.private,
  team: VISIBILITY_LABELS.team,
  player: VISIBILITY_LABELS.player,
  coach: GOAL_VISIBILITY_LABELS.coach,
};

export function SettingsPage() {
  const toast = useToast();
  const settingsQuery = useTeamSettings();
  const mutation = useTeamSettingsMutation();

  // Local edit buffer contains only visible manual defaults. Re-seeded
  // whenever a fresh payload lands (load / save / scope change).
  const [draft, setDraft] = useState<TeamSettings>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    TeamSettingValidationError[]
  >([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const payload = settingsQuery.data;
  useEffect(() => {
    if (payload) setDraft(projectEditableTeamSettings(payload.settings));
  }, [payload]);

  const canEdit = payload?.can_edit ?? false;

  // Dirty check — drives the Save button's enabled state.
  const isDirty = useMemo(
    () => (payload ? isTeamSettingsDirty(payload.settings, draft) : false),
    [draft, payload],
  );

  const setKey = (key: keyof TeamSettings, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    // A fresh edit clears the last save's status.
    setSavedAt(null);
    setSaveError(null);
    setValidationErrors([]);
  };

  const handleSave = async () => {
    if (!payload || !canEdit) return;
    setSaveError(null);
    setValidationErrors([]);
    // Send only the changed keys — the backend applies a partial
    // update; unchanged keys just widen the validation surface.
    const changed = diffTeamSettings(payload.settings, draft);
    try {
      await mutation.save(changed);
      setSavedAt(Date.now());
      toast.success("Team settings saved");
    } catch (err) {
      toast.error("Couldn't save team settings", {
        description: errorMessage(err),
      });
      const apiErr = err as ApiError;
      // `apiFetch` already unwrapped FastAPI's `{ detail: ... }`
      // envelope, so `apiErr.detail` IS the 422 body:
      // `{ code, errors: [{ key, code, detail }] }` for a
      // validation failure, or a plain string otherwise.
      const detail = apiErr?.detail as
        | { code?: string; errors?: TeamSettingValidationError[] }
        | string
        | undefined;
      if (
        detail &&
        typeof detail === "object" &&
        Array.isArray(detail.errors)
      ) {
        setValidationErrors(detail.errors);
      } else {
        setSaveError(
          typeof detail === "string" ? detail : "Could not save team settings.",
        );
      }
    }
  };

  // --- Render guards ---------------------------------------------------

  if (settingsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <PageHeader title="Team Settings" description="Coaching defaults, visibility and guest viewing access." />
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="settings-loading"
        >
          Loading team settings…
        </p>
      </div>
    );
  }

  if (settingsQuery.isError) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <PageHeader title="Team Settings" description="Coaching defaults, visibility and guest viewing access." />
        <p
          className="text-destructive font-mono text-sm px-3 py-6"
          data-testid="settings-load-error"
        >
          Failed to load team settings:{" "}
          {settingsQuery.error instanceof Error
            ? settingsQuery.error.message
            : "unknown error"}
        </p>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <PageHeader title="Team Settings" description="Coaching defaults, visibility and guest viewing access." />
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="settings-no-team"
        >
          No team selected.
        </p>
      </div>
    );
  }

  const schema = payload.schema;

  /** Render one visible default from the server schema. */
  const renderControl = (key: (typeof DEFAULTS_KEYS)[number]) => {
    const entry = schema[key];
    if (!entry || entry.kind !== "enum") return null;
    const labels = SETTING_LABELS[key] ?? { label: key, description: "" };
    const testid = `setting-${key.replace(/\./g, "-")}`;
    let choices = entry.choices ?? [];
    // A player-visibility summary is invisible to viewers.
    if (key === "summaries.default_visibility") {
      choices = choices.filter((choice) => !SUMMARY_VISIBILITY_OMIT.has(choice));
    }
    const options = choices.map((value) => ({
      value,
      label: CHOICE_LABELS[value] ?? value,
    }));
    return (
      <SettingChipGroup
        key={key}
        label={labels.label}
        options={options}
        value={String(draft[key] ?? "")}
        disabled={!canEdit}
        onChange={(next) => setKey(key, next)}
        data-testid={testid}
      />
    );
  };

  return (
    <div className="flex flex-col gap-5" data-testid="settings-page">
      <PageHeader
        title="Team Settings"
        eyebrow="Team controls"
        description={
          <>
            {payload.team.name} · Coaching defaults, visibility and guest
            viewing access.
          </>
        }
        actions={
          !canEdit ? (
            <span
              className="rounded px-2 py-1 text-xs font-display uppercase tracking-wider bg-surface text-ink-muted"
              data-testid="settings-readonly-pill"
            >
              Read only
            </span>
          ) : undefined
        }
      />

      {!canEdit ? (
        <p
          className="text-ink-muted text-sm"
          data-testid="settings-readonly-note"
        >
          A team admin can change coaching defaults, visibility and stream
          settings. You can still create or revoke guest viewing links.
        </p>
      ) : null}

      {/* Separate columns keep defaults beside team visibility and
       * streaming controls on wide screens. */}
      <div className="coach-settings-layout flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-5">
          <AssistTeamPanel />
          {/* Coaching Defaults. */}
          <section
            className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
            aria-labelledby="settings-defaults-heading"
            data-testid="settings-section-defaults"
          >
            <h3
              id="settings-defaults-heading"
              className="text-sm font-display uppercase tracking-wider text-ink-muted"
            >
              Coaching Defaults
            </h3>
            {DEFAULTS_KEYS.map(renderControl)}
          </section>
        </div>

        {/* Visibility + Streaming render for coaches too so the
         * settings surface has the same shape across coach/admin
         * roles. `canEdit` only decides whether write actions are
         * enabled. */}
        <div className="flex flex-1 flex-col gap-5">
          {/* Section 3 (M12a) — Public visibility. Writes are gated on
           * the same team_settings:manage capability as the rest of
           * the page. */}
          <VisibilitySection
            teamId={payload.team.id}
            teamName={payload.team.name}
            isPublic={!!payload.team.public}
            canEdit={canEdit}
          />

          {/* Section 4 (M12c) — Streaming. RTMP ingest URL + public
           * HLS URL + masked stream key + rotate action. */}
          <StreamingSection
            teamId={payload.team.id}
            teamSlug={payload.team.slug}
            teamName={payload.team.name}
            canEdit={canEdit}
          />

          {/* Section 5 (private-beta Phase 4) — Guest links. Expiring,
           * revocable viewing links for account-less family (grandparents).
           * Server gate is `match:write`, which every role that can open
           * Coach Settings holds — no separate canEdit wiring. */}
        </div>
      </div>

      <GuestLinksSection teamId={payload.team.id} teamName={payload.team.name} />

      {/* Membership management lives in Admin > Access — that
       * surface is M8, so this is plain copy for now (no link). */}
      <p className="text-ink-muted text-xs" data-testid="settings-people-note">
        Manage team members and invites in Admin &gt; Access.
      </p>

      {/* Validation errors — one line per offending key. */}
      {validationErrors.length > 0 ? (
        <div
          className="text-destructive font-mono text-xs flex flex-col gap-0.5"
          data-testid="settings-validation-errors"
        >
          {validationErrors.map((e) => (
            <p key={e.key}>
              {e.key}: {e.detail}
            </p>
          ))}
        </div>
      ) : null}
      {saveError ? (
        <p
          className="text-destructive font-mono text-xs"
          data-testid="settings-save-error"
        >
          {saveError}
        </p>
      ) : null}
      {savedAt ? (
        <p
          className="text-ink-muted font-mono text-xs"
          data-testid="settings-saved-note"
        >
          Team settings saved.
        </p>
      ) : null}

      {canEdit ? (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || mutation.isPending}
            data-testid="settings-save"
          >
            {mutation.isPending ? "Saving…" : "Save team settings"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M12a — Public visibility toggle
// ---------------------------------------------------------------------------

/**
 * One-toggle Visibility section: flips ``Team.public``.
 *
 * The "make public" flow goes through `<ConfirmDialog>` because it
 * widens who can read this team's matches; the "make private" flow is
 * a direct PATCH (no confirm) — pulling back from public is a safe
 * retraction. Mirrors the M7.5 destructive-confirm pattern (write-the-
 * confirm-before-the-mutation).
 *
 * On success the active scope is invalidated (so the settings page +
 * any other team-scoped cache picks up the new flag) AND the public-
 * team-directory caches are bumped so a coach navigating to `/teams`
 * immediately sees the updated visibility lane and chip.
 */
function VisibilitySection({
  teamId,
  teamName,
  isPublic,
  canEdit,
}: {
  teamId: string;
  teamName: string;
  isPublic: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const flip = async (next: boolean) => {
    setError(null);
    setPending(true);
    try {
      await setTeamVisibility(teamId, next);
      // Re-fetch the active team's settings (which carries the team
      // row) AND refresh both directory projections so /teams reflects
      // the new flag immediately for public discovery and member access.
      await invalidateScope();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("public-teams"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("accessible-teams"),
        }),
      ]);
      setConfirmOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't update team visibility.",
      );
    } finally {
      setPending(false);
    }
  };

  const onToggle = (next: boolean) => {
    if (!canEdit) return;
    if (next) {
      // Going public — confirm. Going private — no friction.
      setConfirmOpen(true);
    } else {
      void flip(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="settings-visibility-heading"
      data-testid="settings-section-visibility"
    >
      <div className="flex items-center justify-between gap-3">
        <h3
          id="settings-visibility-heading"
          className="text-sm font-display uppercase tracking-wider text-ink-muted"
        >
          Visibility
        </h3>
        <span
          className="settings-visibility-status rounded-full bg-elevated px-3 py-1 text-xs text-ink-muted"
          data-public={isPublic}
          data-testid="settings-visibility-status"
        >
          {isPublic ? "Public" : "Private"}
        </span>
      </div>
      <p className="text-sm text-ink-muted">
        Choose whether the team&rsquo;s public page and match library are
        visible.
      </p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start bg-elevated"
        disabled={!canEdit || pending}
        onClick={() => onToggle(!isPublic)}
        data-testid="setting-team-public"
      >
        {pending
          ? "Updating…"
          : isPublic
            ? "Make team private"
            : "Make team page public"}
      </Button>
      {error ? (
        <p
          data-testid="settings-visibility-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
      {confirmOpen ? (
        <ConfirmDialog
          title="Make this team public?"
          message={
            <p>
              <strong>{teamName}</strong> will appear on the public /teams page.
              Anyone with the link will be able to see this team&rsquo;s matches
              and replays. You can turn this off again at any time.
            </p>
          }
          confirmLabel={pending ? "Updating…" : "Make public"}
          onConfirm={() => void flip(true)}
          onClose={() => {
            if (pending) return;
            setConfirmOpen(false);
            setError(null);
          }}
          data-testid="settings-visibility-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// M12c — Streaming section
// ---------------------------------------------------------------------------

/**
 * Streaming section: RTMP ingest URL + public HLS URL + masked stream
 * key + Rotate action.
 *
 * Privacy invariant — the stream key surfaces ONLY ONCE per rotation
 * (matches the M8b/M9a dev-token pattern). Before the first rotation
 * the masked key shows "Not yet generated"; after rotation we hold the
 * raw key in component state only as long as the operator needs to
 * copy it. The "Rotate key" action goes through `<ConfirmDialog>` —
 * rotation is destructive (it invalidates the live RTMP URL).
 */
function StreamingSection({
  teamId,
  teamSlug,
  teamName,
  canEdit,
}: {
  teamId: string;
  teamSlug: string;
  teamName: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [rotated, setRotated] = useState<{
    key: string;
    streamPath: string;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Last-rotated audit row — refetched on focus so an admin who
  // rotated from /admin/live a moment ago shows up here when the
  // coach comes back to the tab. Refresh isn't aggressive (60s
  // stale) — the notice is informational, not load-bearing.
  const auditQuery = useQuery({
    queryKey: unscopedKey("team-stream-key-audit", { teamId }),
    queryFn: () => getTeamStreamKeyAudit(teamId),
    enabled: canEdit,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // The RTMP URL is the global base (`live_rtmp_public_url` setting,
  // configured in Admin → Settings → Live). We intentionally do NOT
  // fall back to a guessed `rtmp://<host>:1935` URL: a copyable URL
  // that quietly resolves to localhost would mislead a team admin
  // running this in production. When the base isn't configured we
  // show an inline notice instead.
  //
  // The team slug + stream key are NOT appended to this URL — the
  // operator pastes them together into OBS's "Stream Key" field
  // (see the streamKeyField below). OBS concatenates Server + "/" +
  // Stream Key as the RTMP path, so putting the slug in Server too
  // would double it.
  const appSettingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const rtmpBaseRaw = (
    appSettingsQuery.data?.settings?.live_rtmp_public_url ?? ""
  ).trim();
  const rtmpBase = rtmpBaseRaw.replace(/\/+$/, "");
  const rtmpUrl = rtmpBase;
  const hlsUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/live/hls/teams/${teamSlug}/index.m3u8`
      : `/api/live/hls/teams/${teamSlug}/index.m3u8`;
  // Composite Stream Key field — what the operator actually pastes
  // into OBS. Derived from the server's own `stream_path` (multi-club
  // canonical `live/<club>/<team>/<key>`, minus the `live/` prefix the
  // Server URL already carries) so the pasteable value always matches
  // what the auth webhook validates — never rebuilt client-side from
  // the slug. We hide the raw key behind the same Reveal/Hide toggle
  // the bare key used to use.
  const streamKeyField = rotated
    ? rotated.streamPath.replace(/^live\//, "")
    : null;

  const onRotate = async () => {
    setError(null);
    setPending(true);
    try {
      const resp = await rotateTeamStreamKey(teamId);
      setRotated({ key: resp.stream_key, streamPath: resp.stream_path });
      setRevealed(true);
      setConfirmOpen(false);
      // Update the audit query cache with the values the rotate
      // response just echoed back — saves a round-trip and avoids a
      // stale notice between rotate and the next focus refetch.
      queryClient.setQueryData(
        unscopedKey("team-stream-key-audit", { teamId }),
        {
          last_rotated_at: resp.last_rotated_at,
          last_rotated_by: resp.last_rotated_by,
        },
      );
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't rotate the stream key.",
      );
    } finally {
      setPending(false);
    }
  };

  const copyToClipboard = (value: string) => {
    try {
      void navigator.clipboard?.writeText(value);
    } catch {
      // Older browsers / non-secure contexts: leave the value visible
      // so the operator can copy manually.
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="settings-streaming-heading"
      data-testid="settings-section-streaming"
    >
      <h3
        id="settings-streaming-heading"
        className="text-sm font-display uppercase tracking-wider text-ink-muted"
      >
        Streaming
      </h3>
      <p className="text-ink-muted text-xs">
        Encoder connection details for {teamName}.
      </p>

      {rtmpUrl ? (
        <StreamingField
          label="RTMP server URL"
          value={rtmpUrl}
          onCopy={() => copyToClipboard(rtmpUrl)}
          testid="streaming-rtmp-url"
        />
      ) : (
        <div
          data-testid="streaming-rtmp-url-unconfigured"
          className="flex flex-col gap-1.5"
        >
          <span className="font-display text-xs uppercase tracking-wide text-ink-muted">
            RTMP server URL
          </span>
          <p className="rounded border border-rule bg-surface px-3 py-2 font-mono text-[0.7rem] leading-snug text-ink-muted">
            RTMP base URL is not configured. Ask a super admin to set{" "}
            <strong>Live → RTMP public URL</strong> in Admin Settings (e.g.{" "}
            <code>rtmp://stream.example.com/live</code>).
          </p>
        </div>
      )}
      <StreamingField
        label="Public HLS URL"
        value={hlsUrl}
        onCopy={() => copyToClipboard(hlsUrl)}
        testid="streaming-hls-url"
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Stream Key
          </span>
          {streamKeyField ? (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              data-testid="streaming-reveal-toggle"
              className="font-mono text-[0.65rem] text-ink-muted underline hover:text-ink"
            >
              {revealed ? "Hide" : "Reveal"}
            </button>
          ) : null}
        </div>
        <div className="flex items-stretch gap-2">
          <code
            data-testid="streaming-key-value"
            className="flex-1 truncate rounded border border-rule bg-surface px-3 py-2 font-mono text-xs text-ink"
          >
            {streamKeyField
              ? revealed
                ? streamKeyField
                : // Fixed-length mask — slug shown so the operator can
                  // verify the right team without revealing the key.
                  `${teamSlug}/${"•".repeat(24)}`
              : "Not yet generated — rotate to create a key."}
          </code>
          {streamKeyField ? (
            <button
              type="button"
              onClick={() => copyToClipboard(streamKeyField)}
              data-testid="streaming-copy-key"
              className="rounded border border-rule px-3 py-2 font-display text-xs uppercase tracking-wide text-ink-muted hover:text-ink"
            >
              Copy
            </button>
          ) : null}
        </div>
        <p className="text-[0.65rem] text-ink-muted">
          Format: <code>{teamSlug}/&lt;key&gt;</code>. Rotating invalidates any
          encoder still using the old key.
        </p>
        {auditQuery.data?.last_rotated_at ? (
          <p
            data-testid="streaming-last-rotated"
            className="text-[0.65rem] text-ink-muted"
          >
            Last rotated by{" "}
            <strong className="text-ink">
              {auditQuery.data.last_rotated_by ?? "unknown"}
            </strong>{" "}
            at <code>{auditQuery.data.last_rotated_at}</code>. Rotating again
            locks them out.
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          data-testid="streaming-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2 border-t border-rule pt-3">
        {!canEdit ? (
          <p className="text-xs text-ink-muted" data-testid="streaming-readonly">
            Only a team admin can reveal or rotate the stream key.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
            disabled={pending}
            data-testid="streaming-rotate-key"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-display uppercase tracking-wider text-destructive transition-colors hover:bg-destructive/20"
          >
            Rotate key
          </button>
        )}
      </div>

      {confirmOpen ? (
        <ConfirmDialog
          title="Rotate stream key?"
          message={
            <p>
              Generating a new stream key for <strong>{teamName}</strong> will
              immediately reject any in-progress broadcast using the previous
              key. Anyone who has the old key will have to be re-issued the new
              one before they can publish again. This cannot be undone.
            </p>
          }
          confirmLabel={pending ? "Rotating…" : "Rotate key"}
          danger
          onConfirm={() => void onRotate()}
          onClose={() => {
            if (pending) return;
            setConfirmOpen(false);
            setError(null);
          }}
          data-testid="streaming-rotate-confirm"
        />
      ) : null}
    </section>
  );
}

function StreamingField({
  label,
  value,
  onCopy,
  testid,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  testid: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-display uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <div className="flex items-stretch gap-2">
        <code
          data-testid={`${testid}-value`}
          className="flex-1 truncate rounded border border-rule bg-surface px-3 py-2 font-mono text-xs text-ink"
        >
          {value}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className="rounded border border-rule px-3 py-2 font-display text-xs uppercase tracking-wide text-ink-muted hover:text-ink"
        >
          Copy
        </button>
      </div>
    </div>
  );
}
